import {
  FluidCircleTypes,
  FluidLoadingSpinnerConfig,
  FluidSize,
  FluidStyleSize,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  BlockingLoader,
  LoadingSpinner,
} from './components/fluid-loading-spinner.components';

/**
 * @displayName Loading Spinner
 *
 * @contextData FLUID Loading Spinner is an animated visual indication that loading is underway.
 * It is indeterminate, which means that it does not express a specific wait time or display loading progress.
 * Loading Spinner has multiple sizes available, as well as 2 style variations & an option to display a "loading message" beneath the Spinner. It can also be implemented as a container that blocks access to its content while loading.
 *
 * @slot - Content that is loading. All children of this slot will be inaccessible while "loading" Prop is true. NOTE: "container" Prop MUST be true to enable this slot. See documentation for "container" & "loading" Props for more information.
 *
 * @builder true
 */
@Component({
  tag: 'fluid-loading-spinner',
  styleUrl: 'fluid-loading-spinner.scss',
  shadow: true,
})
export class FluidLoadingSpinner
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLFluidLoadingSpinnerElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Single configuration object to populate Spinner (instead of using
   * individual Props).
   */
  @Prop({ mutable: true }) config: FluidLoadingSpinnerConfig;

  @Watch('config')
  handleConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  /**
   * Sets the overall size of the Loading Spinner. Accepts all FluidSize
   * values EXCEPT "xxs" & "full".
   */
  @Prop({ mutable: true }) size: Exclude<
    FluidSize,
    FluidSize.XX_SMALL | FluidSize.FULL
  > = FluidSize.MEDIUM;

  /**
   * Sets the Loading Spinner's aesthetic style & animation behavior.
   */
  @Prop({ mutable: true }) circleType: FluidCircleTypes =
    FluidCircleTypes.NOTCHED;

  /**
   * If true, Spinner will be horizontally centered within its container.
   */
  @Prop({ mutable: true }) centered: boolean;

  /**
   * Sets top & bottom padding of Loading Spinner container. If provided,
   * overrides default 1em top/bottom padding. FluidSize values equate
   * to preset pixel values, decimal numbers between 0 and 1 equate to percent
   * values, whole numbers greater than 1 equate to pixel values, and
   * strings are outputted directly as inline style values (NOTE: strings
   * should contain valid CSS units - i.e. 'px', '%', 'em', 'vh', etc.).
   */
  @Prop({ mutable: true }) verticalPadding: FluidStyleSize;

  /**
   * If provided, displays a message string beneath the Loading Spinner.
   */
  @Prop({ mutable: true }) loadingMessage: string;

  /**
   * NOTE: this Prop MUST be used in conjunction with the "loading" Prop.
   * When true, Loading Spinner will be rendered as a container for slotted
   * content (i.e. enables Loading Spinner's slot). As long as the "loading"
   * Prop is true, the Spinner will be displayed on top of its slotted content
   * & all child elements will be inaccessible. When the "loading" Prop is
   * false (or undefined), the Spinner will be hidden & all slotted children
   * will be accessible (i.e. Loading Spinner becomes a standard <div>).
   */
  @Prop({ mutable: true }) container: boolean;

  /**
   * When loading spinner is used as a blocking container, us this prop to set
   * a minimum pixel height for the blocking spinner, so it can take up an
   * appropriate amount of space on your page until whatever content you are loading
   * can be loaded.
   */
  @Prop({ mutable: true }) height: number;

  /**
   * If true, the loading spinner will be a smaller, more muted spinner. This is
   * useful for inline loading spinners, where the standard size and colour may be
   * too much for the container.
   */
  @Prop({ mutable: true }) muted: boolean;

  /**
   * When true, Spinner will be visible & slotted content will be blocked /
   * inaccessible. When false, Spinner will NOT be displayed, & slotted child
   * elements WILL be accessible (see "container" Prop documentation for more
   * info). NOTE: if "container" Prop is FALSE, this Prop is OPTIONAL & will
   * NOT affect the Spinner's visibility if undefined (i.e. Spinner will always
   * be visible until removed from the DOM). However, if "container" Prop is
   * TRUE, this Prop is REQUIRED to display the Spinner (i.e. Spinner will
   * NOT be visible if this Prop is left undefined).
   */
  @Prop({ mutable: true }) loading: boolean;

  /**
   * Set a unique spinner ID. If there are multiple spinners on a single page,
   * this can be useful when dynamically toggling their loading state.
   */
  @Prop({ mutable: true }) spinnerId: string = generateUniqueId();

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - Set component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    this.size = this.muted ? FluidSize.EXTRA_SMALL : this.size;
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.size = this.normalizeSize(this.size) as Exclude<
      FluidSize,
      FluidSize.XX_SMALL | FluidSize.FULL
    >;
  }

  normalizeSize(value: FluidSize | string): FluidSize {
    if (
      value === FluidSize.XX_SMALL ||
      value === 'xxs' ||
      value === FluidSize.FULL ||
      value === 'full'
    ) {
      return FluidSize.MEDIUM;
    }
    return value as FluidSize;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const props = {
      loadingMessage:
        this._i18nStore?.translate(this.fluidTranslate?.loadingMessage) ||
        this.loadingMessage,
      height: this.height,
      spinnerId: this.spinnerId,
      loading: this.loading,
      zIndex: this.zIndex,
      verticalPadding: this.verticalPadding,
      muted: this.muted,
      size: this.size,
      circleType: this.circleType,
      container: this.container,
      centered: this.centered,
    } as FluidLoadingSpinnerConfig;

    return (
      <div class={`fluid-loading-spinner theme-${this.theme}`}>
        {this.container ? (
          <BlockingLoader {...props} />
        ) : (
          <LoadingSpinner {...props} />
        )}
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for Loading Spinner container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { loadingMessage?: string };
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;
}
